import { Input } from "@heroui/input";
import { CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { FaRegUser } from "react-icons/fa";
import { FaArrowRightLong } from "react-icons/fa6";
import { MdOutlineClose } from "react-icons/md";
import { TfiInfoAlt } from "react-icons/tfi";
import { useMatches } from "react-router";
import { useIsMobile } from "~/hook/use-mobile";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import { useMediaQuery } from "~/hook/useMediaQuery";
import { getLocalStorage, removeItemFromLocalStorage, removeUserLoginKeyFromLocalStorage } from "~/lib/helperFuncation/localStorageService";
import {
  setErrorNull,
  setIsDialogClose,
  setIsDialogOpen,
  setIsFocus,
  setIsPasswordFalse,
  setLoading,
  setLoginStep,
} from "~/lib/store/slice/loginSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { ICON_ORANGE_COLOR, TIMER_KEY, USER_MOBILE_NUMBER } from "~/utils/constant";
import getEnv from "~/utils/get-env";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "../ui/dialog";
import CreatePassword from "./CreatePassword";
import LoginMobileNumber from "./LoginMobileNumber";
import VerifyOtpComponent from "./VerifyOtpComponent";

const ROUTE = ["routes/$lang.share.$type.($token)", "routes/$lang.registration.($token)", "routes/$lang.thalbhet._index.$(token)"];

export function LoginDialog({ isDisplayLoggingIcon = true }: { isDisplayLoggingIcon?: boolean }) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { normalInter } = useFontStyle();
  const { isDialogOpen, isFocus } = useAppSelector((state) => state.login);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const dispatch = useAppDispatch();
  const matches = useMatches();

  const { navigate } = useLocale();
  const currentRouteId = matches[matches.length - 1].id;

  const handleNavigateToHome = () => {
    if (ROUTE.includes(currentRouteId)) {
      dispatch(setIsDialogClose());
      navigate(`/`);
    } else return;
  };

  const onCloseDialog = () => {
    // if (!shouldCloseDialog) return;
    const mobileNumber = getLocalStorage(USER_MOBILE_NUMBER);
    if (mobileNumber) return;

    dispatch(setIsDialogClose());
    removeUserLoginKeyFromLocalStorage();
    dispatch(setErrorNull());
    dispatch(setLoading(false));
    dispatch(setLoginStep(1));
    dispatch(setIsPasswordFalse());
    removeItemFromLocalStorage(TIMER_KEY);
    removeItemFromLocalStorage(USER_MOBILE_NUMBER);
    dispatch(setIsFocus(false));
    handleNavigateToHome();
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      dispatch(setIsDialogOpen());
    } else {
      // Only check reCAPTCHA on desktop (Dialog)
      // const recaptchaFrame = document.querySelector('iframe[src*="google.com/recaptcha/api2/bframe"]');
      // if (recaptchaFrame) {
      //   // reCAPTCHA challenge is open, don't close the dialog
      //   return;
      // }

      !isMobile && onCloseDialog();
    }
  };

  useEffect(() => {
    if (isDialogOpen || isFocus) {
      document.body.classList.add("overflow-hidden");
      document.documentElement.classList.add("overflow-hidden"); // Add this line
    } else {
      document.body.classList.remove("overflow-hidden");
      document.documentElement.classList.remove("overflow-hidden"); // Add this line
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
      document.documentElement.classList.remove("overflow-hidden"); // Add this line
      removeItemFromLocalStorage(USER_MOBILE_NUMBER);
    };
  }, [isDialogOpen, isFocus]);

  return (
    <>
      {isDesktop ? (
        <Dialog modal={false} open={isDialogOpen} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <button
              onClick={onCloseDialog}
              className={cn("w-[96px] py-[9px] px-[23px] bg-buttonColor text-white rounded-3xl font-inter cursor-pointer", normalInter)}
            >
              {t("login_page.login", "Login")}
            </button>
          </DialogTrigger>

          {isDialogOpen && (
            <button
              className="fixed inset-0 bg-black/50 z-40 cursor-default"
              onClick={onCloseDialog}
              onKeyDown={(e) => {
                if (e.key === "Escape") onCloseDialog();
              }}
              aria-label="Close dialog"
              tabIndex={0}
            />
          )}

          <DialogContent isCloseIcon={false} className="z-[100]">
            <LoginContainer isShowCloseButton={true} isShowImage={true} />
          </DialogContent>
        </Dialog>
      ) : (
        <Dialog open={isDialogOpen} onOpenChange={handleOpenChange} modal={false}>
          {isDisplayLoggingIcon && (
            <DialogTrigger asChild>
              <div className="max-md:flex hidden size-8 rounded-full justify-center items-center bg-white border-[1.9px] border-primaryColor">
                <FaRegUser size={14} color={ICON_ORANGE_COLOR} />
              </div>
            </DialogTrigger>
          )}

          {/* step === 3 ? "max-h-[calc(100dvh-250px)] min-h-[calc(100dvh-500px)]" : "max-h-[calc(100dvh-250px)] min-h-[calc(100dvh-400px)] " */}
          <DialogContent
            className={cn(
              "bg-primary max-md:px-4 z-40  flex items-start justify-center w-dvw h-dvh md:max-h-[calc(100dvh-250px)] md:min-h-[calc(100dvh-400px)] overflow-hidden",
            )}
            isCloseIcon={false}
            onTouchMove={(e) => e.preventDefault()}
          >
            <LoginContainer isShowCloseButton={true} isShowImage={false} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

const LoginContainer = ({ isShowImage, isShowCloseButton }: { isShowImage: boolean; isShowCloseButton: boolean }) => {
  const { step } = useAppSelector((state) => state.login);
  const dispatch = useAppDispatch();
  const matches = useMatches();
  const { navigate } = useLocale();
  const currentRouteId = matches[matches.length - 1].id;
  const handleNavigateToHome = () => {
    if (ROUTE.includes(currentRouteId)) {
      dispatch(setIsDialogClose());
      navigate(`/`);
    } else return;
  };

  return (
    <div className="w-full flex justify-start items-center bg-white rounded-[12px] gap-8 max-md:flex-col md:h-full max-md:h-auto max-md:py-4 relative z-50">
      {isShowImage && (
        <div className="w-full md:w-[70%] h-full rounded-[6px] overflow-hidden bg-gray-300">
          <img src="/images/login-image.jpg" alt="login_image" className="w-full h-full select-none" />
        </div>
      )}
      <div className="w-full flex flex-col justify-start items-center  pt-2 md:pt-5 max-md:px-2">
        {step === 1 && <LoginMobileNumber />}
        {step === 2 && <VerifyOtpComponent />}
        {step === 3 && <CreatePassword />}

        {isShowCloseButton && (
          <button
            className={cn(
              "absolute top-1 right-0 size-6 rounded-full bg-secondaryColor flex justify-center items-center",
              step === 3 ? "md:top-0" : " md:top-0",
            )}
            onClick={() => {
              handleNavigateToHome();
              removeUserLoginKeyFromLocalStorage();
              dispatch(setErrorNull());
              dispatch(setIsDialogClose());
              dispatch(setLoginStep(1));
              dispatch(setIsPasswordFalse());
              removeItemFromLocalStorage(TIMER_KEY);
            }}
          >
            <MdOutlineClose className="text-textHeadingColor" />
          </button>
        )}
      </div>
    </div>
  );
};

export const ComponentHeader = ({ title, text, className }: { title: string; text?: string; className?: string }) => {
  const { normalInter, medium } = useFontStyle();
  return (
    // max-md:mt-4
    <div className={cn(`w-full flex flex-col justify-start items-center gap-2`)}>
      <DialogTitle className={cn("text-3xl text-textColor font-medium", medium, className)}>{title}</DialogTitle>
      {text && <span className={cn("w-full text-14 text-textColor font-normal text-center", normalInter)}>{text}</span>}
    </div>
  );
};

export const InputComponent = ({
  type,
  title,
  value,
  onChange,
  className,
  label,
}: {
  type: string;
  title?: string;
  value: string;
  className?: string;
  onChange: (value: string) => void;
  label: string;
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordField = type === "password";

  return (
    <div className="form-common-div relative overflow-hidden">
      {title && <span className="w-full text-textColor text-12 font-inter font-normal capitalize">{title}</span>}

      {/* Input with password toggle */}
      <Input
        label={label}
        type={isPasswordField && showPassword ? "text" : type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full ${className || ""} relative`}
        classNames={{
          label: "!text-textColor pl-3 pb-1 text-14",
          innerWrapper: "bg-secondaryBackground rounded-md !p-3 !ring-0 !outline-none !border-none",
          input: "bg-none !outline-none !text-textColor placeholder:text-textColor !text-[16px] placeholder:text-md pr-10 !ring-0", // extra padding for icon
          inputWrapper:
            "rounded-none !ring-0 group-data-[focus=true]:bg-secondaryBackground group-data-[focus=true]:ring-0 !cursor-text !p-0 shadow-none",
        }}
      />

      {/* Eye Icon for Password */}
      {isPasswordField && (
        <button
          type="button"
          onClick={() => setShowPassword((prev) => !prev)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-textColor z-10 p-1 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primaryColor focus:ring-offset-1 transition-all"
          aria-label={showPassword ? "Hide password" : "Show password"}
          tabIndex={0}
        >
          {showPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
        </button>
      )}
    </div>
  );
};

export const LoginResponseErrorComponent = () => {
  const { error } = useAppSelector((state) => state.login);

  return (
    <div className="w-full flex justify-start items-center bg-red-200 border-[1px] border-red-300 rounded-md p-3 gap-2">
      <TfiInfoAlt size={14} color={"#dc2626"} />
      <span className="text-14 text-red-600 font-inter font-normal">{error}</span>
    </div>
  );
};

export const LoginFlowButton = ({ text, isIcon = true, loadingLogin }: { text: string; isIcon?: boolean; loadingLogin?: boolean }) => {
  const { normalInter } = useFontStyle();
  const { loading, isDisabledNextButton, isPassword, step } = useAppSelector((state) => state.login);
  const env = getEnv();

  const { loading: profileLoading } = useAppSelector((state) => state.profile);
  return (
    <div className="w-full flex flex-col justify-center items-center gap-3">
      <button
        type="submit"
        disabled={step === 1 ? !isPassword && isDisabledNextButton : loading || profileLoading}
        className="g-recaptcha w-full rounded-full py-2.5 px-6 bg-darkOrangeColor flex justify-center items-center gap-2 cursor-pointer disabled:opacity-50"
      >
        {loading || loadingLogin || profileLoading ? (
          <CircularProgress size={20} sx={{ color: "white" }} />
        ) : (
          <>
            <span className={cn("text-14 text-white font-medium", normalInter)}>{text}</span>
            {isIcon && <FaArrowRightLong size={14} color="white" />}
          </>
        )}
      </button>
    </div>
  );
};
